import z from "zod";

import { ChangeRequestOperationType } from "./enums/ChangeRequestOperationType";
import { ChangeRequestStatus } from "./enums/ChangeRequestStatus";
import { ChangeRequestDomain } from "./enums/ChangeRequestDomain";
import {
  ModifierGroupIdScheme,
  ModifierIdScheme,
  PrepStationIdScheme,
  ProductGroupIdScheme,
  ProductIdScheme,
  UnitIdScheme,
  BookkeepingAccountIdScheme,
  FloorPlanIdScheme,
  LayoutIdScheme,
  ProductPageIdScheme,
  UserIdScheme,
} from "./ids";
import { idOrOperationRef } from "./changeRequestRefs";
import {
  ChangeRequestCourseOperationStrictScheme,
  ChangeRequestDeviceConfigOperationStrictScheme,
  ChangeRequestOfferOperationStrictScheme,
  ChangeRequestOnlineOrderSettingsOperationStrictScheme,
  ChangeRequestPaymentMethodOperationStrictScheme,
  ChangeRequestPrinterOperationStrictScheme,
  ChangeRequestStoreSettingsOperationStrictScheme,
  ChangeRequestSupplierOperationStrictScheme,
} from "./changeRequestSettingsOperations";
import {
  ChangeRequestGroupCreatePayloadScheme,
  ChangeRequestGroupUpdateChangesScheme,
  ChangeRequestModifierCreatePayloadScheme,
  ChangeRequestModifierGroupCreatePayloadScheme,
  ChangeRequestModifierGroupUpdateChangesScheme,
  ChangeRequestModifierUpdateChangesScheme,
  ChangeRequestProductCreatePayloadScheme,
  ChangeRequestProductUpdateChangesScheme,
  ChangeRequestUnitCreatePayloadScheme,
  ChangeRequestUnitUpdateChangesScheme,
  ChangeRequestPrepStationCreatePayloadScheme,
  ChangeRequestPrepStationUpdateChangesScheme,
  ChangeRequestBookkeepingAccountCreatePayloadScheme,
  ChangeRequestBookkeepingAccountUpdateChangesScheme,
  ChangeRequestFloorPlanCreatePayloadScheme,
  ChangeRequestFloorPlanUpdateChangesScheme,
  ChangeRequestLayoutCreatePayloadScheme,
  ChangeRequestLayoutUpdateChangesScheme,
  ChangeRequestProductPageAddItemsPayloadScheme,
  ChangeRequestProductPageCreatePayloadScheme,
  ChangeRequestProductPageUpdateChangesScheme,
} from "./changeRequestOperationPayloads";

export { ChangeRequestOperationType, ChangeRequestStatus, ChangeRequestDomain };

export const ChangeRequestProductOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductCreate),
      payload: ChangeRequestProductCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductUpdate),
      payload: z.object({
        productId: ProductIdScheme,
        changes: ChangeRequestProductUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductDelete),
      payload: z.object({
        productId: ProductIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestProductOperation = z.infer<
  typeof ChangeRequestProductOperationStrictScheme
>;

export const ChangeRequestGroupOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.GroupCreate),
      payload: ChangeRequestGroupCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.GroupUpdate),
      payload: z.object({
        groupId: ProductGroupIdScheme,
        changes: ChangeRequestGroupUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.GroupDelete),
      payload: z.object({
        groupId: ProductGroupIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestGroupOperation = z.infer<
  typeof ChangeRequestGroupOperationStrictScheme
>;

export const ChangeRequestModifierOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierCreate),
      payload: ChangeRequestModifierCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierUpdate),
      payload: z.object({
        modifierId: ModifierIdScheme,
        changes: ChangeRequestModifierUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierDelete),
      payload: z.object({
        modifierId: ModifierIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestModifierOperation = z.infer<
  typeof ChangeRequestModifierOperationStrictScheme
>;

export const ChangeRequestModifierGroupOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierGroupCreate),
      payload: ChangeRequestModifierGroupCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierGroupUpdate),
      payload: z.object({
        modifierGroupId: ModifierGroupIdScheme,
        changes: ChangeRequestModifierGroupUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ModifierGroupDelete),
      payload: z.object({
        modifierGroupId: ModifierGroupIdScheme,
      }),
    }),
  ]);

export type ChangeRequestModifierGroupOperation = z.infer<
  typeof ChangeRequestModifierGroupOperationStrictScheme
>;

export const ChangeRequestUnitOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.UnitCreate),
      payload: ChangeRequestUnitCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.UnitUpdate),
      payload: z.object({
        unitId: UnitIdScheme,
        changes: ChangeRequestUnitUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.UnitDelete),
      payload: z.object({
        unitId: UnitIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestUnitOperation = z.infer<
  typeof ChangeRequestUnitOperationStrictScheme
>;

export const ChangeRequestPrepStationOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrepStationCreate),
      payload: ChangeRequestPrepStationCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrepStationUpdate),
      payload: z.object({
        prepStationId: PrepStationIdScheme,
        changes: ChangeRequestPrepStationUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrepStationDelete),
      payload: z.object({
        prepStationId: PrepStationIdScheme,
      }),
    }),
  ]);

export type ChangeRequestPrepStationOperation = z.infer<
  typeof ChangeRequestPrepStationOperationStrictScheme
>;

export const ChangeRequestBookkeepingAccountOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.BookkeepingAccountCreate),
      payload: ChangeRequestBookkeepingAccountCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.BookkeepingAccountUpdate),
      payload: z.object({
        bookkeepingAccountId: BookkeepingAccountIdScheme,
        changes: ChangeRequestBookkeepingAccountUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.BookkeepingAccountDelete),
      payload: z.object({
        bookkeepingAccountId: BookkeepingAccountIdScheme,
      }),
    }),
  ]);

export type ChangeRequestBookkeepingAccountOperation = z.infer<
  typeof ChangeRequestBookkeepingAccountOperationStrictScheme
>;

export const ChangeRequestFloorPlanOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.FloorPlanCreate),
      payload: ChangeRequestFloorPlanCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.FloorPlanUpdate),
      payload: z.object({
        floorPlanId: FloorPlanIdScheme,
        changes: ChangeRequestFloorPlanUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.FloorPlanDelete),
      payload: z.object({
        floorPlanId: FloorPlanIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestFloorPlanOperation = z.infer<
  typeof ChangeRequestFloorPlanOperationStrictScheme
>;

export const ChangeRequestLayoutOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.LayoutCreate),
      payload: ChangeRequestLayoutCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.LayoutUpdate),
      payload: z.object({
        layoutId: idOrOperationRef(LayoutIdScheme),
        changes: ChangeRequestLayoutUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.LayoutDelete),
      payload: z.object({
        layoutId: LayoutIdScheme,
      }),
    }),
  ],
);

export type ChangeRequestLayoutOperation = z.infer<
  typeof ChangeRequestLayoutOperationStrictScheme
>;

export const ChangeRequestProductPageOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductPageCreate),
      payload: ChangeRequestProductPageCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductPageUpdate),
      payload: z.object({
        productPageId: idOrOperationRef(ProductPageIdScheme),
        changes: ChangeRequestProductPageUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductPageAddItems),
      payload: ChangeRequestProductPageAddItemsPayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.ProductPageDelete),
      payload: z.object({
        productPageId: ProductPageIdScheme,
      }),
    }),
  ]);

export type ChangeRequestProductPageOperation = z.infer<
  typeof ChangeRequestProductPageOperationStrictScheme
>;

export const ChangeRequestOperationStrictScheme = z.discriminatedUnion("type", [
  ...ChangeRequestProductOperationStrictScheme.options,
  ...ChangeRequestGroupOperationStrictScheme.options,
  ...ChangeRequestModifierOperationStrictScheme.options,
  ...ChangeRequestModifierGroupOperationStrictScheme.options,
  ...ChangeRequestUnitOperationStrictScheme.options,
  ...ChangeRequestPrepStationOperationStrictScheme.options,
  ...ChangeRequestBookkeepingAccountOperationStrictScheme.options,
  ...ChangeRequestFloorPlanOperationStrictScheme.options,
  ...ChangeRequestLayoutOperationStrictScheme.options,
  ...ChangeRequestProductPageOperationStrictScheme.options,
  ...ChangeRequestStoreSettingsOperationStrictScheme.options,
  ...ChangeRequestOnlineOrderSettingsOperationStrictScheme.options,
  ...ChangeRequestDeviceConfigOperationStrictScheme.options,
  ...ChangeRequestPaymentMethodOperationStrictScheme.options,
  ...ChangeRequestPrinterOperationStrictScheme.options,
  ...ChangeRequestCourseOperationStrictScheme.options,
  ...ChangeRequestOfferOperationStrictScheme.options,
  ...ChangeRequestSupplierOperationStrictScheme.options,
]);

export type ChangeRequestOperation = z.infer<
  typeof ChangeRequestOperationStrictScheme
>;

export const ChangeRequestOperationsStrictScheme = z.array(
  ChangeRequestOperationStrictScheme,
);

export const ChangeRequestAppliedOperationScheme = z.object({
  operationId: z.string(),
  createdId: z.string().nullable(),
  derivedIds: z.record(z.string(), z.string()),
});

export type ChangeRequestAppliedOperation = z.infer<
  typeof ChangeRequestAppliedOperationScheme
>;

export const ChangeRequestContextEntryScheme = z.object({
  id: z.string(),
  displayName: z.string(),
  record: z.record(z.string(), z.unknown()),
});

export type ChangeRequestContextEntry = z.infer<
  typeof ChangeRequestContextEntryScheme
>;

export const ChangeRequestContextScheme = z.partialRecord(
  z.enum(ChangeRequestDomain),
  z.record(z.string(), ChangeRequestContextEntryScheme),
);

export type ChangeRequestContext = z.infer<typeof ChangeRequestContextScheme>;

export const ChangeRequestCoreScheme = z.object({
  id: z.string(),
  storeId: z.string(),
  chatSessionId: z.string(),
  status: z.enum(ChangeRequestStatus),
  operations: ChangeRequestOperationsStrictScheme,
  appliedOperations: z.array(ChangeRequestAppliedOperationScheme).optional(),
  approvedAt: z.iso.datetime().optional(),
  approvedBy: UserIdScheme.optional(),
  approvedContext: ChangeRequestContextScheme.optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type ChangeRequest = z.infer<typeof ChangeRequestCoreScheme>;

export const ChangeRequestBodyScheme = ChangeRequestCoreScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const ChangeRequestViewScheme = ChangeRequestCoreScheme.extend({
  context: ChangeRequestContextScheme,
});

export type ChangeRequestView = z.infer<typeof ChangeRequestViewScheme>;
